import {createRoot, Root} from 'react-dom/client';
import {BalloonEventsMapOptions} from '../app/types/EmbedOptions';
import {MetadataLoader} from './MetadataLoader';
import {EventBus} from './EventBus';
import {configureApiFetch, getBackendConfig} from 'ts-core';

/**
 * Core map instance class that manages lifecycle, events, and metadata.
 * Each instance is independent with its own state and event system.
 */
export class BalloonEventsMapInstance {
    private id: string;
    private root: Root | null = null;
    private container: HTMLElement | null = null;
    private options: BalloonEventsMapOptions;
    private metadata: any = null;
    private eventBus: EventBus;
    private metadataLoader: MetadataLoader;
    private apis: Map<string, Function> = new Map();
    private apiObject: Record<string, any> | null = null;
    public static instance: BalloonEventsMapInstance | null = null;

    constructor(options: BalloonEventsMapOptions) {
        this.id = this.generateUniqueId();
        this.options = {...options, rootId: this.id};
        this.eventBus = new EventBus(this.id);
        this.metadataLoader = new MetadataLoader(options.key);
        BalloonEventsMapInstance.instance = this;

        // Bootstrap apiFetch global config so all requests include correct headers
        try {
            const backendConfig = getBackendConfig();
            configureApiFetch({
                apiKey: backendConfig.apiKey,
                partnerKey: options.key || null,
            });
        } catch {
            // getBackendConfig may throw if env vars are missing; fall back to options.key
            configureApiFetch({ partnerKey: options.key || null });
        }
    }

    /**
     * Generate a unique instance ID
     */
    private generateUniqueId(): string {
        return `balloon-map-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    }

    /**
     * Get the unique instance ID
     */
    public getId(): string {
        return this.id;
    }

    /**
     * Load metadata (with caching)
     */
    private loadAbortController: AbortController | null = null;

    private async loadMetadata(): Promise<void> {
        console.log('[BalloonEventsMapInstance] loadMetadata called', {
            instanceId: this.id,
            alreadyLoaded: !!this.metadata,
        });

        if (this.metadata) {
            console.log('[BalloonEventsMapInstance] Metadata already loaded, skipping');
            return;
        }

        // Create a new AbortController for this metadata load
        this.loadAbortController = new AbortController();
        
        try {
            console.log('[BalloonEventsMapInstance] Loading metadata...');
            this.metadata = await this.metadataLoader.load({
                signal: this.loadAbortController.signal
            });
            console.log('[BalloonEventsMapInstance] Metadata loaded:', this.metadata);
        } catch (error) {
            if ((error as Error).name === 'AbortError') {
                console.log('[BalloonEventsMapInstance] Metadata loading was aborted');
                return;
            }
            console.error('[BalloonEventsMapInstance] Error loading metadata:', error);
            throw error;
        } finally {
            this.loadAbortController = null;
        }
    }
    
    // Track if the instance is mounted
    private isMounted = false;
    
    /**
     * Render the map to a DOM element
     * @param targetElement - DOM element or selector string
     */

    async render(targetElement: HTMLElement | string): Promise<void> {
        console.log('[BalloonEventsMapInstance] render() called', { targetElement });

        try {
            // Resolve target element
            let container: HTMLElement | null = null;
            
            if (typeof targetElement === 'string') {
                container = document.getElementById(targetElement) as HTMLElement;
                if (!container) {
                    console.warn(`[BalloonEventsMapInstance] Target element not found: ${targetElement}`);
                    return;
                }
            } else {
                container = targetElement;
            }

            // Check if container is still in the DOM
            if (!container || !container.isConnected) {
                console.warn('[BalloonEventsMapInstance] Container is not connected to the DOM');
                return;
            }

            this.container = container;
            console.log('[BalloonEventsMapInstance] Container resolved:', this.container);

            // Ensure element has an ID for event handling
            if (!this.container.id) {
                this.container.id = this.id;
            }

            // Add embed class for styling
            this.container.classList.add('be-embedded-app');
            this.container.style.minWidth = '0';

            // Mark as mounted before async operations
            this.isMounted = true;

            // Load metadata before rendering
            console.log('[BalloonEventsMapInstance] About to load metadata...');
            await this.loadMetadata();
            
            // Check again if still mounted after async operation
            if (!this.isMounted || !this.container?.isConnected) {
                console.warn('[BalloonEventsMapInstance] Container was unmounted during metadata load');
                return;
            }

            console.log('[BalloonEventsMapInstance] Metadata loading complete');

            // Create React root and render
            // Final check before creating React root
            if (!this.isMounted || !this.container?.isConnected) {
                console.warn('[BalloonEventsMapInstance] Instance destroyed before React root creation');
                return;
            }

            console.log('[BalloonEventsMapInstance] Creating React root...');
            this.root = createRoot(this.container);
            console.log('[BalloonEventsMapInstance] React root created successfully');

            // Import and render App component dynamically
            const {default: App} = await import('../app/App');
            const React = await import('react');
            const {StrictMode} = React;

            this.root.render(
                React.createElement(
                    StrictMode,
                    null,
                    React.createElement(App, {
                        options: this.options,
                        metadata: this.metadata,
                        clientEmbedAPI: this.getAPIObject(),
                        clientEmbedEventTrigger: (event: string, data?: any) => {
                            this.trigger(event, data);
                        },
                    })
                )
            );

            // Trigger ready event
            this.trigger('ready', {
                instanceId: this.id,
                options: this.options,
            });
        } catch (error) {
            // Only log errors if not due to instance being destroyed
            if (this.isMounted) {
                console.error('[BalloonEventsMapInstance] Error rendering map:', error);
            } else {
                console.log('[BalloonEventsMapInstance] Render cancelled - instance was destroyed');
            }
        }
    }

    /**
     * Register an event listener
     * @param event - Event name
     * @param callback - Callback function
     */
    public on(event: string, callback: Function): void {
        const extendedCallback = (...data: any) => {
            callback.call(this, ...data);
        }
        this.eventBus.on(event, extendedCallback);
    }

    /**
     * Unregister an event listener
     * @param event - Event name
     * @param callback - Callback function
     */
    public off(event: string, callback: Function): void {
        this.eventBus.off(event, callback);
    }

    /**
     * Trigger an event
     * @param event - Event name
     * @param data - Event data
     */
    public trigger(event: string, data?: any): void {
        // Dispatch DOM CustomEvent for Web Component compatibility
        if (this.container) {
            this.container.dispatchEvent(
                new CustomEvent(event, {
                    detail: data,
                    bubbles: true,
                })
            );
        }

        // Trigger internal event bus
        this.eventBus.trigger(event, data);
    }

    /**
     * Register an API method
     * @param name - API method name
     * @param fn - Function to execute
     */
    public registerAPI(name: string, fn: Function): void {
        this.apis.set(name, fn);
    }

    /**
     * Get API value or execute API method
     * @param name - API name
     * @returns API result
     */
    public get(name: string): any {
        // First check the API object (populated by ClientEmbedAPIProvider)
        if (this.apiObject && name in this.apiObject) {
            const api = this.apiObject[name];
            return typeof api === 'function' ? api() : api;
        }
        
        // Fall back to the Map
        const api = this.apis.get(name);
        if (!api) {
            console.warn(`API method not found: ${name}`);
            return undefined;
        }
        return api();
    }

    /**
     * Get the API object for passing to App component
     */
    private getAPIObject(): Record<string, any> {
        if (!this.apiObject) {
            this.apiObject = {};
            
            // Convert Map to object for easier access
            this.apis.forEach((fn, name) => {
                this.apiObject![name] = fn;
            });
        }

        return this.apiObject;
    }
    /**
     * Check if the instance is still mounted and the container is in the DOM
     */
    private isStillMounted(): boolean {
        if (!this.isMounted) {
            console.log('[BalloonEventsMapInstance] Instance is not mounted');
            return false;
        }
        
        if (!this.container || !this.container.isConnected) {
            console.log('[BalloonEventsMapInstance] Container is not connected to the DOM');
            this.isMounted = false;
            return false;
        }
        
        return true;
    }

    /**
     * Destroy the instance and clean up resources
     */
    public destroy(): void {
        console.log(`[BalloonEventsMapInstance] Destroying instance: ${this.id}`);
        
        // Mark as unmounted to prevent further operations
        this.isMounted = false;
        
        // Abort any in-progress metadata loading
        if (this.loadAbortController) {
            this.loadAbortController.abort('Instance destroyed');
            this.loadAbortController = null;
        }

        // Clean up React root if it exists
        if (this.root) {
            try {
                this.root.unmount();
            } catch (error) {
                console.warn('[BalloonEventsMapInstance] Error while unmounting React root:', error);
            }
            this.root = null;
        }
        
        // Clean up container references and classes
        if (this.container) {
            try {
                if (this.container.isConnected) {
                    this.container.removeAttribute('id');
                    this.container.classList.remove('be-embedded-app');
                }
            } catch (error) {
                console.warn('[BalloonEventsMapInstance] Error while cleaning up container:', error);
            }
            this.container = null;
        }
        
        // Clean up event bus
        this.eventBus.destroy();
        
        // Clear any stored metadata
        this.metadata = null;
        
        // Clear any registered APIs
        this.apis.clear();
        
        console.log(`[BalloonEventsMapInstance] Instance ${this.id} destroyed`);
    }

    /**
     * Get current metadata
     */
    public getMetadata() {
        return this.metadata;
    }

    /**
     * Update options and trigger re-render
     * @param newOptions - Partial options to update
     */
    public async updateOptions(newOptions: Partial<BalloonEventsMapOptions>): Promise<void> {
        console.log('[BalloonEventsMapInstance] Updating options:', newOptions);
        
        // Merge new options with existing ones
        this.options = {
            ...this.options,
            ...newOptions,
        };
        
        // If we have a root and container, re-render with updated options
        if (this.root && this.container && this.isStillMounted()) {
            const {default: App} = await import('../app/App');
            const React = await import('react');
            const {StrictMode} = React;

            this.root.render(
                React.createElement(
                    StrictMode,
                    null,
                    React.createElement(App, {
                        options: this.options,
                        metadata: this.metadata,
                        clientEmbedAPI: this.getAPIObject(),
                        clientEmbedEventTrigger: (event: string, data?: any) => {
                            this.trigger(event, data);
                        },
                    })
                )
            );
            
            console.log('[BalloonEventsMapInstance] Options updated and re-rendered');
        }
    }

    /**
     * Get current options
     */
    public getOptions(): BalloonEventsMapOptions {
        return this.options;
    }
}
